import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import { CheckIcon } from 'components/ReusableComponents/Icons/Check'
import { CrossIcon } from 'components/ReusableComponents/Icons/Cross'
import RefreshIcon from 'components/ReusableComponents/RefreshIcon'
import Toggle from 'components/ReusableComponents/Toggle/index'
import { hotelCampaignRegistrationStatuses, hotelStatusNamesFilterable } from 'main/hotelStatuses'
import { parentChainNames, PMSnames } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { getHealthCheckSyncData } from '../../../actions/healthCheck'
import CustomHeader from '../../../components/ReusableComponents/HeaderTitles'
import LoadingIndicator from '../../../components/ReusableComponents/LoadingIndicator'
import Table from '../../../components/ReusableComponents/Table'
import Tab from '../../../components/ReusableComponents/Tabs'
import downloadAsCsv from '../../../utils/downloadAsCsv'
import {
  calculatePmsHealthCheckRowStatus,
  getPmsHealthCheckRowStatusExplanation,
  resolvePmsHealthCheckRowStatusSortRank
} from './calculatePmsHealthCheckRowStatus'
import {
  matchesPepHealthFilters,
  formatPepHealthCheckLastResultForExport,
  getPepHealthCheckDisplay
} from './parsePepHealthCheckLastResult'
import { buildPmsTableSyncSummary } from './pmsTableSyncSummary'
import {
  derivePepDesktopAppVersionState,
  getPepDesktopAppVersionDisplay,
  matchesPepDesktopAppVersionFilter
} from './pepDesktopAppVersion'
import {
  derivePepCsmAction,
  getPepCsmActionFilterLabel,
  matchesPepCsmActionFilter,
  resolvePepCsmActionSortRank,
  sanitizePepCsmActionFilterKeys
} from './derivePepCsmAction'
import PepCsmActionFilter from './PepCsmActionFilter'
import { useLatestDesktopAppRelease } from './useLatestDesktopAppRelease'
import { parseHealthCheckPmsDeepLinkQuery } from './healthCheckPmsDeepLinks'
import {
  getEffectivePmsReportsInLast48Hours,
  isPmsSyncLastInfoStale
} from './pmsSyncDataRecency'
import HealthCheckFilters from '../Common/Filters'
import useHealthCheckFilters from '../Common/useHealthCheckFilters'
import { NoteBatchLoaderProvider, useNoteBatchLoader } from '../Common/NoteBatchLoaderContext'
import { ReactComponent as ExcelIcon } from '../Billing/excel.svg'
import GroupedRow from '../Billing/GroupedRow'
import styles from './PMSandBE.module.scss'
import SyncRow from './Row'
import { ReactComponent as BugIcon } from './Row/bug.svg'
import PMSBEHealthTitleRow from './TitleRow'

/** Column index -> row property for sorting (null = not sortable). Must match PMSBEHealthTitleRow + SyncRow cell order. */
const PMS_TABLE_SORT_KEYS_FULL = [
  'displayId',
  'shortName',
  null,
  'csmUserFullName',
  'isAllReportHealthy',
  'pmsSyncLastInfoReceivedOn',
  'pmsSyncTotalReportsReceivedInLast48Hours',
  'pms',
  'lastResult',
  null,
  null
]

/** Same as full table minus Stop / Test login (PEP-only view). */
const PMS_TABLE_SORT_KEYS_NO_STOP = [
  'displayId',
  'shortName',
  null,
  'csmUserFullName',
  'isAllReportHealthy',
  'pmsSyncLastInfoReceivedOn',
  'pmsSyncTotalReportsReceivedInLast48Hours',
  'pms',
  'lastResult',
  null
]

/** Compact PEP-only table (no Stop/Test login column). Must match TitleRow + SyncRow cell order. */
const PMS_TABLE_SORT_KEYS_PEP_COMPACT = [
  'displayId',
  'shortName',
  'csmUserFullName',
  'pmsSyncLastInfoReceivedOn',
  '__pepCsmAction',
  'lastResult',
  null,
  null
]

const PMS_TABLE_WIDTHS_FULL = [40, 220, 10, 50, 100, 110, 90, 140, 380, 140, 350]
const PMS_TABLE_WIDTHS_NO_STOP = [40, 220, 10, 50, 100, 110, 90, 140, 520, 350]
const PMS_TABLE_WIDTHS_PEP_COMPACT = [52, 220, 44, 118, 144, 118, 112, 220]
const PEP_BADGE_SORT_ORDER = {
  Working: 0,
  PepAuthorizationError: 1,
  NeedsAttention: 2,
  AppNotInstalled: 3
}

function resolvePepBadgeSortRank(hotel) {
  if (!hotel || hotel.pms !== 'pep') {
    return Number.MAX_SAFE_INTEGER
  }
  const pepDisplay = getPepHealthCheckDisplay(
    hotel.lastResult,
    hotel.pms,
    getEffectivePmsReportsInLast48Hours(
      hotel.pmsSyncLastInfoReceivedOn,
      hotel.pmsSyncTotalReportsReceivedInLast48Hours
    )
  )
  if (!pepDisplay) {
    return Number.MAX_SAFE_INTEGER
  }
  return PEP_BADGE_SORT_ORDER[pepDisplay.badgeKey] ?? Number.MAX_SAFE_INTEGER
}

const HealthCheckPMS = () => {
  const dispatch = useDispatch()
  const location = useLocation()

  const [debugData, setDebugData] = useState()
  const [telemetryOpenSubsidiaryId, setTelemetryOpenSubsidiaryId] = useState(null)
  const deepLinkAppliedRef = useRef(false)

  const [filterState, filterDispatch] = useHealthCheckFilters('pms')

  const [groupByHotelGroup, setGroupByHotelGroup] = useState(() => {
    const saved = localStorage.getItem('healthCheck-pms-groupByHotelGroup')
    return saved === 'true'
  })
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const saved = localStorage.getItem('healthCheck-pms-expandedGroups')
    return saved ? JSON.parse(saved) : {}
  })

  useEffect(() => {
    dispatch(getHealthCheckSyncData())
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading = useSelector(s => s.healthCheck.sync.isLoading)
  const hotelData = useSelector(s => s.healthCheck.sync.hotelData)
  const allhotelGroups = useSelector(s => s.healthCheck.sync.hotelGroups)
  const allhotelChains = useSelector(s => s.healthCheck.sync.hotelChains)

  useEffect(() => {
    if (deepLinkAppliedRef.current || isLoading || !hotelData || hotelData.length === 0) {
      return
    }

    const parsed = parseHealthCheckPmsDeepLinkQuery(location.search)
    if (!parsed.hasDeepLink) {
      return
    }

    if (parsed.pepOnly) {
      filterDispatch({ type: 'pms', payload: ['pep'] })
    }

    if (parsed.queuePreset) {
      filterDispatch({ type: 'pms', payload: ['pep'] })
    }

    if (parsed.actionKey) {
      filterDispatch({ type: 'pms', payload: ['pep'] })
      filterDispatch({ type: 'pepCsmActions', payload: [parsed.actionKey] })
    }

    let searchQuery = parsed.searchQuery
    if (!searchQuery && parsed.subsidiaryId) {
      const matchedHotel = hotelData.find(h => h.subsidiaryId === parsed.subsidiaryId)
      if (matchedHotel?.displayId != null) {
        searchQuery = String(matchedHotel.displayId)
      }
    }

    if (searchQuery) {
      filterDispatch({ type: 'searchString', payload: searchQuery })
    }

    if (parsed.openTelemetry && parsed.subsidiaryId) {
      setTelemetryOpenSubsidiaryId(parsed.subsidiaryId)
    }

    deepLinkAppliedRef.current = true
  }, [isLoading, hotelData, location.search, filterDispatch])

  const [sortByIndex, setSortByIndex] = useState(4)
  const [sortDecreasing, setSortDecreasing] = useState(false)
  const [didApplyPepDefaultSort, setDidApplyPepDefaultSort] = useState(false)

  // Alert status filter toggles - persisted in localStorage
  const { showAlert, showWarning, showGreen } = filterState
  const showPepDesktopAppControls = filterState.pms.includes('pep')
  const { latestRelease: latestDesktopRelease, isLoading: isLoadingDesktopRelease } =
    useLatestDesktopAppRelease(showPepDesktopAppControls)

  const calculateRowStatus = calculatePmsHealthCheckRowStatus
  const getStatusExplanation = getPmsHealthCheckRowStatusExplanation

  function handleTitleCellClick(rowIndex) {
    if (sortByIndex === rowIndex) {
      setSortDecreasing(d => !d)
    }

    setSortByIndex(rowIndex)
  }

  function getFilteredResults(hotelData, options = {}) {
    const { skipDesktopAppVersionFilter = false, skipPepCsmActionFilter = false } = options
    const {
      searchString,
      chains,
      brands,
      hotelGroups,
      subsidiaryStatus,
      pms,
      csmIds,
      pepHealthBadgeKeys,
      pepOnboardingStates,
      pepFutureStates,
      pepDesktopAppVersionFilter,
      pepCsmActions: rawPepCsmActions
    } = filterState
    const pepCsmActions = sanitizePepCsmActionFilterKeys(rawPepCsmActions)

    const safeSearch = (searchString || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const filtered = hotelData
      .filter(h => (h.searchKey || '').match(new RegExp(safeSearch, 'i')))
      .filter(h => (hotelGroups === 'all' ? true : h.groupName === hotelGroups))
      .filter(h => (chains === 'all' ? true : h.chainCode === chains))
      .filter(h => (brands === 'all' ? true : h.parentChainCode === brands))
      .filter(h => {
        if (pms.length === 0) {
          return true
        } else {
          return pms.includes(h.pms)
        }
      })
      .filter(h => {
        if (subsidiaryStatus.length === 0) {
          return true
        } else if (hotelCampaignRegistrationStatuses.includes(h.subsidiaryStatus)) {
          return subsidiaryStatus.includes('PendingCampaignBrandRegistration')
        } else {
          return subsidiaryStatus.includes(h.subsidiaryStatus)
        }
      })
      .filter(h => {
        if (csmIds.length === 0) {
          return true
        } else {
          return csmIds.includes(`${h.csmUserId}`)
        }
      })
      .filter(h => {
        if (h.pms !== 'pep') {
          return true
        }
        const pepDisplay = getPepHealthCheckDisplay(
          h.lastResult,
          h.pms,
          getEffectivePmsReportsInLast48Hours(
            h.pmsSyncLastInfoReceivedOn,
            h.pmsSyncTotalReportsReceivedInLast48Hours
          )
        )
        return matchesPepHealthFilters(pepDisplay, {
          badgeKeys: pepHealthBadgeKeys,
          onboardingStates: pepOnboardingStates,
          futureStates: pepFutureStates
        })
      })

    // Apply status filter
    const statusFiltered = filtered.filter(h => {
      const status = calculateRowStatus(h)
      if (status === 'alert') return showAlert
      if (status === 'warning') return showWarning
      if (status === 'green') return showGreen
      return true
    })

    const csmActionFiltered = skipPepCsmActionFilter
      ? statusFiltered
      : statusFiltered.filter(h => {
          if (h.pms !== 'pep') {
            return true
          }
          const action = derivePepCsmAction(h)
          return matchesPepCsmActionFilter(action.actionKey, pepCsmActions)
        })

    if (
      skipDesktopAppVersionFilter ||
      !pms.includes('pep') ||
      pepDesktopAppVersionFilter === 'all'
    ) {
      return csmActionFiltered
    }

    return csmActionFiltered.filter(h => {
      if (h.pms !== 'pep') {
        return false
      }

      const versionState = derivePepDesktopAppVersionState(h, latestDesktopRelease)
      return matchesPepDesktopAppVersionFilter(versionState, pepDesktopAppVersionFilter)
    })
  }

  function getSortedResults(hotelData, sortBy, sortDecreasing, sortKeys) {
    const keys = sortKeys || PMS_TABLE_SORT_KEYS_FULL
    const sortByProp =
      sortBy >= 0 && sortBy < keys.length ? keys[sortBy] : null

    const compareSecondary = (a, b) => {
      if (!sortByProp) {
        return 0
      }

      const av = a[sortByProp]
      const bv = b[sortByProp]

      if (sortByProp === 'lastResult') {
        const pepRankA = resolvePepBadgeSortRank(a)
        const pepRankB = resolvePepBadgeSortRank(b)
        if (pepRankA !== pepRankB) {
          return pepRankA - pepRankB
        }
      }

      if (sortByProp === '__pepCsmAction') {
        return resolvePepCsmActionSortRank(a) - resolvePepCsmActionSortRank(b)
      }

      if (
        sortByProp === 'bookingEngineLastInfoReceivedOn' ||
        sortByProp === 'pmsSyncLastInfoReceivedOn' ||
        sortByProp === 'nextStartDate' ||
        sortByProp === 'nextRunDateTime'
      ) {
        return (bv ? Date.parse(bv) : 0) - (av ? Date.parse(av) : 0)
      }

      if (typeof av === 'boolean' || typeof bv === 'boolean') {
        return Number(bv === true) - Number(av === true)
      }

      if (typeof av === 'string' || typeof bv === 'string') {
        const as = (av ?? '').toString().toUpperCase()
        const bs = (bv ?? '').toString().toUpperCase()
        if (as === bs) {
          return 0
        }
        return as > bs ? 1 : -1
      }

      const an = Number(av)
      const bn = Number(bv)
      if (!Number.isNaN(an) && !Number.isNaN(bn)) {
        return bn - an
      }
      return 0
    }

    // Always group by sync status first (green → warning → alert) so the list is not mixed.
    // Column sort direction applies only within each status group.
    return [...hotelData].sort((a, b) => {
      const statusCmp =
        resolvePmsHealthCheckRowStatusSortRank(a) -
        resolvePmsHealthCheckRowStatusSortRank(b)
      if (statusCmp !== 0) {
        return statusCmp
      }
      const secondary = compareSecondary(a, b)
      return sortDecreasing ? -secondary : secondary
    })
  }

  const filteredHotels = useMemo(() => {
    if (!hotelData || hotelData.length === 0) {
      return []
    }
    return getFilteredResults(hotelData)
  }, [
    hotelData,
    filterState,
    showAlert,
    showWarning,
    showGreen,
    latestDesktopRelease
  ])

  const hideStopTestLoginColumn = useMemo(
    () => filteredHotels.length > 0 && filteredHotels.every(h => h.pms === 'pep'),
    [filteredHotels]
  )

  const useCompactPepLayout = hideStopTestLoginColumn

  const activeSortKeys = useCompactPepLayout
    ? PMS_TABLE_SORT_KEYS_PEP_COMPACT
    : hideStopTestLoginColumn
    ? PMS_TABLE_SORT_KEYS_NO_STOP
    : PMS_TABLE_SORT_KEYS_FULL

  const activeTableWidths = useCompactPepLayout
    ? PMS_TABLE_WIDTHS_PEP_COMPACT
    : hideStopTestLoginColumn
    ? PMS_TABLE_WIDTHS_NO_STOP
    : PMS_TABLE_WIDTHS_FULL

  const results = useMemo(
    () =>
      getSortedResults(filteredHotels, sortByIndex, sortDecreasing, activeSortKeys),
    [filteredHotels, sortByIndex, sortDecreasing, activeSortKeys]
  )

  useEffect(() => {
    if (!useCompactPepLayout) {
      return
    }
    const maxIdx = PMS_TABLE_SORT_KEYS_PEP_COMPACT.length - 1
    if (sortByIndex > maxIdx) {
      setSortByIndex(maxIdx)
    }
  }, [useCompactPepLayout, sortByIndex])

  useEffect(() => {
    if (!useCompactPepLayout) {
      if (didApplyPepDefaultSort) {
        setDidApplyPepDefaultSort(false)
      }
      return
    }
    if (!didApplyPepDefaultSort && sortByIndex === 4) {
      setDidApplyPepDefaultSort(true)
    }
  }, [useCompactPepLayout, didApplyPepDefaultSort, sortByIndex])

  const pmsTableSyncSummary = useMemo(() => buildPmsTableSyncSummary(results), [results])

  // Prepare subsidiary IDs for batch loading
  const subsidiaryIdsForBatchLoading = useMemo(() => {
    return results.map(h => ({
      subsidiaryId: h.subsidiaryId,
      kind: 'healthCheck-pms'
    }))
  }, [results])

  // Get notes cache for CSV export
  const PMSContent = () => {
    const { notesCache } = useNoteBatchLoader()

    // Kept inside PMSContent: this component is re-created on every HealthCheckPMS render, so
    // highlight state held in the parent would remount all rows and close the telemetry modal
    // the moment View details is clicked.
    const [highlightedTelemetrySubsidiaryId, setHighlightedTelemetrySubsidiaryId] =
      useState(null)

    function handleClickDownloadAsCsv() {
      // Get filter values for metadata
      const {
        searchString,
        chains,
        brands,
        hotelGroups,
        subsidiaryStatus,
        pms,
        csmIds,
        pepHealthBadgeKeys,
        pepOnboardingStates,
        pepFutureStates,
        pepCsmActions
      } = filterState

      // Helper function to get filter label
      const getFilterLabel = (value, allOptions, defaultLabel) => {
        if (value === 'all' || !value || (Array.isArray(value) && value.length === 0)) {
          return 'All'
        }
        if (Array.isArray(value)) {
          return value.map(v => allOptions[v] || v).join(', ')
        }
        return allOptions[value] || value || defaultLabel
      }

      // Get CSM names if available
      const getCsmLabel = () => {
        if (!csmIds || csmIds.length === 0) return 'All'
        return csmIds.join(', ')
      }

      // Get status labels
      const getStatusLabel = () => {
        if (!subsidiaryStatus || subsidiaryStatus.length === 0) return 'All'
        return subsidiaryStatus.map(s => hotelStatusNamesFilterable[s] || s).join(', ')
      }

      // Get PMS labels
      const getPmsLabel = () => {
        if (!pms || pms.length === 0) return 'All'
        return pms.map(p => PMSnames[p] || p).join(', ')
      }

      const formatPepCsmActionFilterForCsv = () => {
        if (!pms || !pms.includes('pep')) {
          return 'N/A'
        }
        const sanitizedPepCsmActions = sanitizePepCsmActionFilterKeys(pepCsmActions)
        if (!sanitizedPepCsmActions || sanitizedPepCsmActions.length === 0) {
          return 'All'
        }
        return sanitizedPepCsmActions.map(k => getPepCsmActionFilterLabel(k)).join(', ')
      }

      const filterMetadata = [
        ['Filter Values Applied:'],
        ['Hotel Group', getFilterLabel(hotelGroups, allhotelGroups, 'All')],
        ['Brand', getFilterLabel(brands, parentChainNames, 'All')],
        ['Chain', getFilterLabel(chains, allhotelChains, 'All')],
        ['Status', getStatusLabel()],
        ['PMS', getPmsLabel()],
        ['CSM action (Hilton PEP)', formatPepCsmActionFilterForCsv()],
        [
          'Desktop app (Hilton PEP)',
          filterState.pms.includes('pep')
            ? filterState.pepDesktopAppVersionFilter || 'all'
            : 'N/A'
        ],
        ['CSM', getCsmLabel()],
        ['Search', searchString || 'None'],
        ['Status Filters', `Alert: ${showAlert ? 'Yes' : 'No'}, Warning: ${showWarning ? 'Yes' : 'No'}, Green: ${showGreen ? 'Yes' : 'No'}`],
        [] // Empty row separator
      ]

      const titleRow = [
        'Hotel ID',
        'Hotel Name',
        'Hotel Group',
        'Brand',
        'Chain',
        'CSM',
        'Status',
        'CSM action',
        'CSM action instruction',
        'PMS Sync',
        'Last SYNC',
        'Reports for last 48h',
        'PMS',
        'Last result',
        'Desktop app',
        'Service Start Date',
        'Date Added',
        'Notes'
      ]

      const resAsArrays = results.map(h => {
        const status = calculateRowStatus(h)
        const csmAction = derivePepCsmAction(h)
        let pmsSyncStatus = '-'
        if (h.isDisabled) {
          pmsSyncStatus = 'Disabled'
        } else if (
          h.isAllReportHealthy &&
          !isPmsSyncLastInfoStale(h.pmsSyncLastInfoReceivedOn)
        ) {
          pmsSyncStatus = 'Connected'
        } else {
          pmsSyncStatus = 'Not Connected'
        }

        // Get note from cache if available
        const noteKey = `${h.subsidiaryId}-healthCheck-pms`
        const note = notesCache?.get(noteKey) || '-'

        return [
          h.displayId,
          `${h.brand || ''} ${h.shortName || ''}`.trim(),
          h.groupName || '-',
          h.parentChainCode || '-',
          h.chainCode || '-',
          h.csmUserFullName || '-',
          h.subsidiaryStatus ? (hotelStatusNamesFilterable[h.subsidiaryStatus] || h.subsidiaryStatus) : '-',
          csmAction.actionLabel,
          csmAction.actionInstruction,
          pmsSyncStatus,
          h.pmsSyncLastInfoReceivedOn
            ? moment.utc(h.pmsSyncLastInfoReceivedOn).local().format('MM/DD/YYYY h:mm A')
            : h.nextStartDate
            ? moment(h.nextStartDate).add(-1, 'day').format('MM/DD/YYYY')
            : '-',
          h.pmsSyncTotalReportsReceivedInLast48Hours || 0,
          h.pms ? (PMSnames[h.pms] || h.pms) : '-',
          h.lastResult
            ? formatPepHealthCheckLastResultForExport(
                h.lastResult,
                h.pms,
                getEffectivePmsReportsInLast48Hours(
                  h.pmsSyncLastInfoReceivedOn,
                  h.pmsSyncTotalReportsReceivedInLast48Hours
                )
              ).slice(0, 120)
            : '-',
          h.pms === 'pep'
            ? `${getPepDesktopAppVersionDisplay(h, latestDesktopRelease).versionText} (${getPepDesktopAppVersionDisplay(h, latestDesktopRelease).label})`
            : '-',
          h.serviceStartDate
            ? moment(h.serviceStartDate).format('MM/DD/YYYY')
            : '-',
          h.addedOn
            ? moment(h.addedOn).format('MM/DD/YYYY')
            : '-',
          note
        ]
      })

      // Combine metadata, title row, and data
      const dataArray = [
        ...filterMetadata,
        titleRow,
        ...resAsArrays
      ]
      
      const fileName = `health_check_pms_${moment().format('YYYY-MM-DD')}.csv`

      downloadAsCsv(dataArray, fileName)
    }

    return (
      <>
        {debugData && (
          <ConfirmationDialog title="Problem details" onClose={() => setDebugData(null)}>
            <pre>
              {JSON.stringify(JSON.parse(debugData.replace(/(\r\n|\n|\r)/gm, '')), null, 4)}
            </pre>
          </ConfirmationDialog>
        )}
        <HealthCheckFilters
          state={filterState}
          dispatch={filterDispatch}
          allhotelGroups={allhotelGroups}
          allhotelChains={allhotelChains}
          showSubsidiaryStatus
          showPMSFilter
          showCsmFilter
        >
          <div className={styles.alertStatusControls}>
            {showPepDesktopAppControls && (
              <PepCsmActionFilter
                selectedActions={filterState.pepCsmActions || []}
                onChangeActions={values =>
                  filterDispatch({ type: 'pepCsmActions', payload: values })
                }
              />
            )}
            <div className={styles.groupByControl}>
              <Toggle
                checked={groupByHotelGroup}
                handleChange={e => {
                  const newValue = e.target.checked
                  setGroupByHotelGroup(newValue)
                  localStorage.setItem('healthCheck-pms-groupByHotelGroup', String(newValue))
                }}
                label="Group by Hotel Group"
                className={styles.toggle}
              />
            </div>
            <div className={styles.statusToggleControls}>
              <span className={styles.alertStatusControlsTitle}>Filter by Status:</span>
              <Toggle
                className={styles.toggle}
                checked={showAlert}
                handleChange={e => filterDispatch({ type: 'showAlert', payload: e.target.checked })}
                label="Alert"
              />
              <Toggle
                className={styles.toggle}
                checked={showWarning}
                handleChange={e => filterDispatch({ type: 'showWarning', payload: e.target.checked })}
                label="Warning"
              />
              <Toggle
                className={styles.toggle}
                checked={showGreen}
                handleChange={e => filterDispatch({ type: 'showGreen', payload: e.target.checked })}
                label="Green"
              />
            </div>
          </div>
          <SecondaryRegularButton
            className={styles.copyToExcelButton}
            onClick={handleClickDownloadAsCsv}
          >
            <ExcelIcon /> Download as CSV
          </SecondaryRegularButton>
        </HealthCheckFilters>

        <div
          className={[
            styles.tableContainer,
            useCompactPepLayout ? styles.pepCompactTableGrid : ''
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {isLoading ? (
            <LoadingIndicator marginTop={100} />
          ) : (
            <>
              <div className={styles.legends}>
                <div className={styles.legend}>
                  <CheckIcon width={16} height={16} /> PMS is connected
                </div>
                <div className={styles.legend}>
                  <CrossIcon width={16} height={16} /> PMS is not connected
                </div>
                <div className={styles.legend}>
                  <CrossIcon width={16} height={16} fill="red" />
                  Not synced for 14 days and turned off{' '}
                </div>
                <div className={styles.legend}>
                  <BugIcon width={22} fill="red" /> PMS sync problems
                </div>
              </div>
              <div className={styles.syncSummaryBar} role="status" aria-live="polite">
                <span className={styles.syncSummaryTitle}>PMS sync (current filters)</span>
                <span className={styles.syncSummaryItem}>
                  <strong>{pmsTableSyncSummary.total}</strong> hotels
                </span>
                <span className={styles.syncSummaryItem}>
                  · Sync OK: <strong>{pmsTableSyncSummary.green}</strong>
                </span>
                <span className={styles.syncSummaryItem}>
                  · Warning: <strong>{pmsTableSyncSummary.warning}</strong>
                </span>
                <span className={styles.syncSummaryItem}>
                  · Alert: <strong>{pmsTableSyncSummary.alert}</strong>
                </span>
                {showPepDesktopAppControls ? (
                  <span className={styles.syncSummaryItem}>
                    · Latest release:{' '}
                    <strong>
                      {isLoadingDesktopRelease ? '…' : latestDesktopRelease || 'Unknown'}
                    </strong>
                  </span>
                ) : null}
              </div>
              <Table
                widths={activeTableWidths}
                handleTitleCellClick={handleTitleCellClick}
                sortIndex={sortByIndex}
                sortDirection={sortDecreasing ? 'down' : 'up'}
              >
                <PMSBEHealthTitleRow
                  hideStopTestLogin={hideStopTestLoginColumn}
                  compactPepLayout={useCompactPepLayout}
                />
                {groupByHotelGroup
                  ? (() => {
                      const grouped = results.reduce((acc, hotel) => {
                        const groupName = hotel.groupName || 'Ungrouped'
                        if (!acc[groupName]) {
                          acc[groupName] = []
                        }
                        acc[groupName].push(hotel)
                        return acc
                      }, {})

                      const sortedGroups = Object.keys(grouped).sort()

                      let globalIndex = 1
                      return sortedGroups.map(groupName => {
                        const groupHotels = grouped[groupName]
                        const groupKey = groupName || 'ungrouped'
                        const isExpanded = expandedGroups[groupKey] !== false

                        return (
                          <GroupedRow
                            key={groupKey}
                            groupName={groupName}
                            count={groupHotels.length}
                            isExpanded={isExpanded}
                            onToggle={() => {
                              setExpandedGroups(prev => {
                                const newState = {
                                  ...prev,
                                  [groupKey]: !isExpanded
                                }
                                localStorage.setItem(
                                  'healthCheck-pms-expandedGroups',
                                  JSON.stringify(newState)
                                )
                                return newState
                              })
                            }}
                          >
                            {groupHotels.map(h => {
                              const rowStatus = calculateRowStatus(h)
                              const statusExplanation = getStatusExplanation(h)
                              const currentIndex = globalIndex++
                              return (
                                <SyncRow
                                  key={h.subsidiaryId}
                                  {...h}
                                  index={currentIndex}
                                  rowStatus={rowStatus}
                                  statusExplanation={statusExplanation}
                                  handleSetDebugData={() => setDebugData(h.problems)}
                                  hideStopTestLogin={hideStopTestLoginColumn}
                                  compactPepLayout={useCompactPepLayout}
                                  latestDesktopRelease={latestDesktopRelease}
                                  showDesktopAppVersion={showPepDesktopAppControls}
                                  openTelemetryOnMount={
                                    telemetryOpenSubsidiaryId === h.subsidiaryId
                                  }
                                  isTelemetryHighlighted={
                                    highlightedTelemetrySubsidiaryId === h.subsidiaryId
                                  }
                                  onTelemetryHighlight={() =>
                                    setHighlightedTelemetrySubsidiaryId(h.subsidiaryId)
                                  }
                                />
                              )
                            })}
                          </GroupedRow>
                        )
                      })
                    })()
                  : results.map((h, i) => {
                      const rowStatus = calculateRowStatus(h)
                      const statusExplanation = getStatusExplanation(h)
                      return (
                        <SyncRow
                          key={h.subsidiaryId}
                          {...h}
                          index={i + 1}
                          rowStatus={rowStatus}
                          statusExplanation={statusExplanation}
                          handleSetDebugData={() => setDebugData(h.problems)}
                          hideStopTestLogin={hideStopTestLoginColumn}
                          compactPepLayout={useCompactPepLayout}
                          latestDesktopRelease={latestDesktopRelease}
                          showDesktopAppVersion={showPepDesktopAppControls}
                          openTelemetryOnMount={
                            telemetryOpenSubsidiaryId === h.subsidiaryId
                          }
                          isTelemetryHighlighted={
                            highlightedTelemetrySubsidiaryId === h.subsidiaryId
                          }
                          onTelemetryHighlight={() =>
                            setHighlightedTelemetrySubsidiaryId(h.subsidiaryId)
                          }
                        />
                      )
                    })}
              </Table>
            </>
          )}
        </div>
      </>
    )
  }

  return (
    <NoteBatchLoaderProvider
      subsidiaryIds={subsidiaryIdsForBatchLoading}
      kind="healthCheck-pms"
    >
      <div className={styles.wrapper}>
        {debugData && (
          <ConfirmationDialog title="Problem details" onClose={() => setDebugData(null)}>
            <pre>
              {JSON.stringify(JSON.parse(debugData.replace(/(\r\n|\n|\r)/gm, '')), null, 4)}
            </pre>
          </ConfirmationDialog>
        )}
        <CustomHeader title="Hotel health check" width="100%" isFixed />
      <Tab.Parent>
        <Tab to="/healthCheck/pms">PMS</Tab>
        <Tab to="/healthCheck/autoinvite">AutoInvite</Tab>
        <Tab to="/healthCheck/billing">Billing</Tab>
        <Tab to="/healthCheck/performance">Performance</Tab>
        <Tab to="/healthCheck/summary">Summary</Tab>
        <button
          onClick={() => dispatch(getHealthCheckSyncData())}
          className={styles.reloadButton}
        >
          <RefreshIcon /> Reload results
        </button>
      </Tab.Parent>
      <PMSContent />
    </div>
    </NoteBatchLoaderProvider>
  )
}

export default HealthCheckPMS
